import React, { useEffect, useState } from 'react';
import { useFossasSepticas } from '../context/FossasSepticasProvider';
import { formatStatus } from '../utils/formatters';
import { toast } from 'react-hot-toast';

/**
 * Componente para distribuição de propriedades entre agentes
 */
const DistribuicaoPropriedades = () => {
  const { 
    propriedades, 
    agentes, 
    carregarPropriedades, 
    carregarAgentes, 
    atribuirPropriedade, 
    loading 
  } = useFossasSepticas();

  const [selectedAgente, setSelectedAgente] = useState('');
  const [propriedadesNaoAtribuidas, setPropriedadesNaoAtribuidas] = useState([]);
  const [propriedadesAtribuidas, setPropriedadesAtribuidas] = useState([]);
  const [filtroLocalidade, setFiltroLocalidade] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');

  // Carregar dados ao montar o componente
  useEffect(() => {
    carregarPropriedades();
    carregarAgentes();
  }, [carregarPropriedades, carregarAgentes]);

  // Atualizar listas de propriedades quando o agente selecionado mudar
  useEffect(() => {
    if (propriedades.length > 0) {
      // Filtrar propriedades não atribuídas
      let naoAtribuidas = propriedades.filter(p => !p.agente_id);
      
      // Aplicar filtros
      if (filtroLocalidade) {
        naoAtribuidas = naoAtribuidas.filter(p => p.localidade_id === parseInt(filtroLocalidade));
      }
      
      if (filtroStatus) {
        naoAtribuidas = naoAtribuidas.filter(p => p.status === filtroStatus);
      }
      
      setPropriedadesNaoAtribuidas(naoAtribuidas);
      
      // Filtrar propriedades atribuídas ao agente selecionado
      if (selectedAgente) {
        let atribuidas = propriedades.filter(p => p.agente_id === parseInt(selectedAgente));
        
        // Aplicar filtros
        if (filtroLocalidade) {
          atribuidas = atribuidas.filter(p => p.localidade_id === parseInt(filtroLocalidade));
        }
        
        if (filtroStatus) {
          atribuidas = atribuidas.filter(p => p.status === filtroStatus);
        }
        
        setPropriedadesAtribuidas(atribuidas);
      } else {
        setPropriedadesAtribuidas([]);
      }
    }
  }, [propriedades, selectedAgente, filtroLocalidade, filtroStatus]);

  // Atribuir propriedade ao agente selecionado
  const handleAtribuir = async (propriedadeId) => {
    if (!selectedAgente) {
      toast.error('Selecione um agente primeiro');
      return;
    }
    
    const success = await atribuirPropriedade(propriedadeId, parseInt(selectedAgente));
    
    if (success) {
      // Atualizar listas
      const propriedade = propriedadesNaoAtribuidas.find(p => p.id === propriedadeId);
      
      setPropriedadesNaoAtribuidas(prev => prev.filter(p => p.id !== propriedadeId));
      setPropriedadesAtribuidas(prev => [...prev, { ...propriedade, agente_id: parseInt(selectedAgente) }]);
    }
  };

  // Remover atribuição de propriedade
  const handleRemoverAtribuicao = async (propriedadeId) => {
    const success = await atribuirPropriedade(propriedadeId, null);
    
    if (success) {
      // Atualizar listas
      const propriedade = propriedadesAtribuidas.find(p => p.id === propriedadeId);
      
      setPropriedadesAtribuidas(prev => prev.filter(p => p.id !== propriedadeId));
      setPropriedadesNaoAtribuidas(prev => [...prev, { ...propriedade, agente_id: null }]);
    }
  };

  // Limpar filtros
  const limparFiltros = () => {
    setFiltroLocalidade('');
    setFiltroStatus('');
  };

  return (
    <div className="card">
      <h2>Distribuição de Propriedades</h2>
      
      <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Agente</label>
            <select
              value={selectedAgente}
              onChange={(e) => setSelectedAgente(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">Selecione um agente</option>
              {agentes.filter(a => a.ativo).map(agente => (
                <option key={agente.id} value={agente.id}>{agente.nome}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Filtrar por Localidade</label>
            <select
              value={filtroLocalidade}
              onChange={(e) => setFiltroLocalidade(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">Todas as localidades</option>
              {Array.from(new Set(propriedades.map(p => p.localidade_id))).map(localidadeId => {
                const localidade = propriedades.find(p => p.localidade_id === localidadeId)?.localidade;
                return localidade ? (
                  <option key={localidade.id} value={localidade.id}>{localidade.descricao}</option>
                ) : null;
              })}
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Filtrar por Status</label>
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">Todos os status</option>
              <option value="Mapeada">Mapeada</option>
              <option value="Não Mapeada">Não Mapeada</option>
              <option value="Em Análise">Em Análise</option>
            </select>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button 
              onClick={limparFiltros}
              style={{ 
                marginTop: '20px',
                padding: '8px 16px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                border: '1px solid #ced4da',
                backgroundColor: '#f8f9fa',
                color: '#333'
              }}
            >
              Limpar Filtros
            </button>
          </div>
        </div>
      </div>
      
      {/* Estatísticas rápidas */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: '15px',
        marginBottom: '25px'
      }}>
        <div style={{ 
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '15px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          border: '1px solid #d6d8db',
          borderLeft: '5px solid #6c757d'
        }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#383d41', fontSize: '16px' }}>Total de Propriedades</h3>
          <div style={{ fontSize: '28px', fontWeight: 'bold', margin: '10px 0', color: '#333' }}>
            {propriedades.length}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>Propriedades cadastradas</div>
        </div>
        
        <div style={{ 
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '15px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          border: '1px solid #f5c6cb',
          borderLeft: '5px solid #dc3545'
        }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#721c24', fontSize: '16px' }}>Não Atribuídas</h3>
          <div style={{ fontSize: '28px', fontWeight: 'bold', margin: '10px 0', color: '#333' }}>
            {propriedades.filter(p => !p.agente_id).length}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>Propriedades sem agente responsável</div>
        </div>
        
        <div style={{ 
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '15px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          border: '1px solid #c3e6cb',
          borderLeft: '5px solid #28a745'
        }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#155724', fontSize: '16px' }}>Atribuídas</h3>
          <div style={{ fontSize: '28px', fontWeight: 'bold', margin: '10px 0', color: '#333' }}>
            {propriedades.filter(p => p.agente_id).length}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>Propriedades com agente responsável</div>
        </div>
        
        <div style={{ 
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '15px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          border: '1px solid #b8daff',
          borderLeft: '5px solid #007bff'
        }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#004085', fontSize: '16px' }}>Agentes Ativos</h3>
          <div style={{ fontSize: '28px', fontWeight: 'bold', margin: '10px 0', color: '#333' }}>
            {agentes.filter(a => a.ativo).length}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>Agentes disponíveis para atribuição</div>
        </div>
      </div>

      {loading.propriedades || loading.agentes ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ 
            border: '4px solid rgba(0, 0, 0, 0.1)',
            borderLeftColor: '#2e7d32',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p>Carregando dados...</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '15px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '15px', paddingBottom: '10px', borderBottom: '1px solid #e9ecef' }}>
              Propriedades Não Atribuídas
            </h3>
            {propriedadesNaoAtribuidas.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                <p>Não há propriedades não atribuídas.</p>
              </div>
            ) : (
              <div className="propriedades-grid">
                {propriedadesNaoAtribuidas.map(prop => {
                  const statusInfo = formatStatus(prop.status);
                  return (
                    <div key={prop.id} className="propriedade-card">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h3 style={{ margin: 0, fontSize: '18px' }}>{prop.nome}</h3>
                        <span 
                          style={{ 
                            padding: '4px 8px', 
                            borderRadius: '4px', 
                            fontSize: '12px', 
                            fontWeight: 'bold', 
                            color: 'white', 
                            backgroundColor: statusInfo.color 
                          }}
                        >
                          {statusInfo.text}
                        </span>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px', fontSize: '14px', marginBottom: '15px' }}>
                        <div>
                          <strong>Proprietário:</strong> {prop.nome_proprietario}
                        </div>
                        <div>
                          <strong>Localidade:</strong> {prop.localidade?.nome || 'Não definida'}
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <button 
                          onClick={() => handleAtribuir(prop.id)}
                          disabled={!selectedAgente}
                          style={{ 
                            width: '100%',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: !selectedAgente ? 'not-allowed' : 'pointer',
                            border: 'none',
                            backgroundColor: '#2e7d32',
                            color: 'white',
                            opacity: !selectedAgente ? 0.7 : 1
                          }}
                        >
                          Atribuir ao Agente
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '15px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '15px', paddingBottom: '10px', borderBottom: '1px solid #e9ecef' }}>
              Propriedades Atribuídas
            </h3>
            {!selectedAgente ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                <p>Selecione um agente para ver suas propriedades atribuídas.</p>
              </div>
            ) : propriedadesAtribuidas.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                <p>Este agente não possui propriedades atribuídas.</p>
              </div>
            ) : (
              <div className="propriedades-grid">
                {propriedadesAtribuidas.map(prop => {
                  const statusInfo = formatStatus(prop.status);
                  return (
                    <div key={prop.id} className="propriedade-card">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h3 style={{ margin: 0, fontSize: '18px' }}>{prop.nome}</h3>
                        <span 
                          style={{ 
                            padding: '4px 8px', 
                            borderRadius: '4px', 
                            fontSize: '12px', 
                            fontWeight: 'bold', 
                            color: 'white', 
                            backgroundColor: statusInfo.color 
                          }}
                        >
                          {statusInfo.text}
                        </span>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px', fontSize: '14px', marginBottom: '15px' }}>
                        <div>
                          <strong>Proprietário:</strong> {prop.nome_proprietario}
                        </div>
                        <div>
                          <strong>Localidade:</strong> {prop.localidade?.nome || 'Não definida'}
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <button 
                          onClick={() => handleRemoverAtribuicao(prop.id)}
                          style={{ 
                            width: '100%',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            border: 'none',
                            backgroundColor: '#dc3545',
                            color: 'white'
                          }}
                        >
                          Remover Atribuição
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .propriedades-grid {
          display: flex;
          flex-direction: column;
          gap: 15px;
          max-height: 600px;
          overflow-y: auto;
          padding-right: 5px;
        }
        
        .propriedade-card {
          background-color: white;
          border-radius: 8px;
          padding: 15px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          overflow: hidden;
          border: 1px solid #e9ecef;
        }
        
        @media (max-width: 768px) {
          .distribuicao-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default DistribuicaoPropriedades;